import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';
import cn from 'classnames';
import parsePhoneNumber, { isValidPhoneNumber } from 'libphonenumber-js';

import {
  Button,
  Checkbox,
  Header,
  Icons,
  Link,
  Text,
  Wrapper,
} from 'widget/components';
import {
  getFromLocalStorage,
  isolateNumbers,
  parseFromLocalStorage,
  putInLocalStorage,
} from 'widget/actions';
import { useIsWorkingTime, useWhatcrmApi } from 'widget/hooks';
import { useWidgetContext } from 'widget/widget-context';
import { withActive } from 'widget/hoc/with-active';
import * as Env from 'widget/env';

// eslint-disable-next-line
const { version } = require('../../../../../package.json');

import Closer from './closer/closer';
// import Message from './message/message';
import Name from './name/name';
import PhoneNumber from './phone-number/phone-number';

interface Values {
  message: string;
  name: string;
  phone_number: string;
}

interface Modal {
  isActive: boolean;
  setIsActive?: Env.SetState<boolean>;
}

const isUrl = (text: string): boolean => /^https:\/\/[^.]+\..{2,}$/.test(text);

const Modal = ({ isActive, setIsActive }: Modal) => {
  const { widget } = useWidgetContext();

  const [isChecked, setIsChecked] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number>();
  const [status, setStatus] = useState<'error' | 'success' | null>(null);

  const { postManagersCallback } = useWhatcrmApi();
  const { t } = useTranslation();
  const isWorkingTime = useIsWorkingTime();
  const windowRef = useRef<HTMLDivElement>(null);

  const callback = (e: TouchEvent | MouseEvent) => {
    if (!setIsActive) return;

    const target = e.target as HTMLDivElement | null;

    if (!target) return;
    else if (windowRef.current?.contains(target)) return;

    setIsActive(false);
  };

  const updateMaxHeight = (): void => {
    const rect = windowRef.current?.getBoundingClientRect();
    if (!rect) return;

    const nextMaxHeight =
      window.innerHeight - (window.innerHeight - rect.bottom) - 16;

    setMaxHeight(nextMaxHeight);
  };

  useEffect(() => {
    document.addEventListener('mouseup', callback);
    document.addEventListener('touchend', callback);

    if (widget.type == 'horizontal') {
      updateMaxHeight();
      window.addEventListener('resize', updateMaxHeight);
    }

    return () => {
      document.removeEventListener('mouseup', callback);
      document.removeEventListener('touchend', callback);
      window.removeEventListener('resize', updateMaxHeight);
    };
  }, []);

  const handleSubmit = async (values: Values): Promise<boolean> => {
    putInLocalStorage(
      'whatcrmWidgetGuest',
      JSON.stringify({ name: values.name, phone: values.phone_number }),
    );

    if (
      !widget.callback_extension_id ||
      !widget.callback_num ||
      !widget.crm_param_id
    ) {
      setStatus('error');
      return true;
    }

    const userId = getFromLocalStorage(
      `whatcrmWidgetUserId${widget.instance_chat_key}`,
    );

    const data = await postManagersCallback(widget.crm_param_id, {
      caller_id_name: values.name,
      chat_key: widget.instance_chat_key,
      dst_ani: widget.callback_ani,
      dst_num: values.phone_number,
      extension_id: widget.callback_extension_id,
      src_num: widget.callback_num,
      user_id: userId,
      userId,
    });

    setStatus(data ? 'success' : 'error');
    return true;
  };

  const testPhoneNumber = (value: string | undefined): boolean => {
    if (!value) return false;

    const numbers = isolateNumbers(value);
    if (!numbers) return false;

    const normalized = `+${numbers}`;
    const parsed = parsePhoneNumber(normalized);

    if (!parsed?.country) return false;
    const isValid = isValidPhoneNumber(value ?? '', parsed.country);

    return isValid;
  };

  const guest = parseFromLocalStorage<Partial<Env.Guest>>('whatcrmWidgetGuest');

  const initialValues: Values = {
    message: '',
    name: guest?.name ?? '',
    phone_number: guest?.phone ?? '',
  };

  const validationSchema = yup.object().shape({
    name: yup.string().trim().required(),
    phone_number: yup.string().test(testPhoneNumber),
  });

  return (
    <div
      className={cn('whatcrm-widget-modal', {
        'whatcrm-widget-modal_active': isActive,
      })}
    >
      <div
        className="whatcrm-widget-modal__window"
        ref={windowRef}
        style={{ maxHeight }}
      >
        {widget.type != 'horizontal' && <Closer setIsActive={setIsActive} />}

        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validationSchema={validationSchema}
        >
          {({ isSubmitting }) => (
            <Form>
              <div className="whatcrm-widget-modal__inner">
                <Header.H2 style={{ marginBottom: 24 }}>
                  {widget.callback_header || t`Request a callback`}
                </Header.H2>

                {widget.privacy && isUrl(widget.privacy) ? (
                  status == 'error' ? (
                    <Wrapper
                      alignItems="center"
                      flexDirection="column"
                      gap={24}
                    >
                      <Wrapper
                        alignItems="center"
                        flexDirection="column"
                        gap={8}
                      >
                        <Icons.PhoneX fill="red" size={48} />

                        <Text textAlign="center">
                          {t`Unfortunately, we were unable to process your request at this time.`}
                        </Text>
                      </Wrapper>

                      {setIsActive && (
                        <Button onClick={() => setIsActive?.(false)}>
                          {t`Close`}
                        </Button>
                      )}
                    </Wrapper>
                  ) : status == 'success' ? (
                    <Wrapper
                      alignItems="center"
                      flexDirection="column"
                      gap={24}
                    >
                      <Wrapper
                        alignItems="center"
                        flexDirection="column"
                        gap={8}
                      >
                        <Icons.CheckCircle fill="green" size={48} />

                        <Text textAlign="center">
                          {t`Your request has been submitted! Please wait for our call.`}
                        </Text>
                      </Wrapper>

                      {setIsActive && (
                        <Button onClick={() => setIsActive?.(false)}>
                          {t`OK`}
                        </Button>
                      )}
                    </Wrapper>
                  ) : isWorkingTime ? (
                    <>
                      <Name style={{ marginBottom: 24 }} />
                      <PhoneNumber style={{ marginBottom: 24 }} />

                      <Checkbox
                        isChecked={isChecked}
                        label={
                          <Trans
                            values={{
                              text: widget.callback_button_text || t`Submit`,
                            }}
                          >
                            {
                              'By clicking the “{{text}}” button, I consent to the processing of my personal data (name, phone number) in accordance with the '
                            }
                            <Link href={widget.privacy}>
                              {'Privacy Policy'}
                            </Link>
                            {'.'}
                          </Trans>
                        }
                        onChange={setIsChecked}
                        style={{ marginBottom: 24 }}
                      />

                      {/* {widget.callback_is_message && (
                      <Message style={{ marginBottom: 24 }} />
                    )} */}

                      <Button
                        isDisabled={!isChecked}
                        isSubmitting={isSubmitting}
                        type="submit"
                      >
                        {widget.callback_button_text || t`Submit`}
                      </Button>
                    </>
                  ) : (
                    <Wrapper
                      alignItems="center"
                      flexDirection="column"
                      gap={24}
                    >
                      <Wrapper
                        alignItems="center"
                        flexDirection="column"
                        gap={8}
                      >
                        <Icons.ClockCountdown fill="gray" size={48} />

                        <Text textAlign="center">
                          {widget.unavailable_text ||
                            t`Sorry, our managers are currently unavailable.`}
                        </Text>
                      </Wrapper>

                      {setIsActive && (
                        <Button onClick={() => setIsActive?.(false)}>
                          {t`Close`}
                        </Button>
                      )}
                    </Wrapper>
                  )
                ) : (
                  <Wrapper alignItems="center" flexDirection="column" gap={24}>
                    <Wrapper alignItems="center" flexDirection="column" gap={8}>
                      <Icons.Info fill="gray" size={48} />

                      <Text textAlign="center">
                        {t`We’re sorry, but we cannot accept your request at this time.`}{' '}
                        {t`A Privacy Policy is required.`}
                      </Text>
                    </Wrapper>

                    {setIsActive && (
                      <Button onClick={() => setIsActive?.(false)}>
                        {t`Close`}
                      </Button>
                    )}
                  </Wrapper>
                )}

                <p
                  style={{
                    color: 'gray',
                    fontSize: '12px',
                    lineHeight: '16px',
                    marginTop: 24,
                    textAlign: 'center',
                  }}
                >
                  {'v'}
                  {version}
                </p>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

const withActiveModal = withActive(Modal);
export default withActiveModal;
